import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { downloadApiExport, getDateForFileName, translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";

import type { ETransferTermSalesStatus, ITransferTermSale } from "./transferTermSalesStore";
import type { TFetchStatus, TParamsPagination } from "./types";

// Get all transfer terms
export const fetchTransferTerms = createAsyncThunk(
    "transferTerms/fetchTransferTerms",
    async ({ options }: { options: TParamsPagination }, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/transfer-term`, {
                params: options,
            });
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Get one transfer term
export const fetchTransferTerm = createAsyncThunk(
    "transferTerms/fetchTransferTerm",
    async ({ transferTermId }: { transferTermId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/transfer-term/${transferTermId}`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Create a transfer term
export const createTransferTerm = createAsyncThunk(
    "transferTerms/createTransferTerm",
    async (
        {
            data,
        }: {
            data: Omit<
                ITransferTerm,
                | "id"
                | "createdAt"
                | "modifiedAt"
                | "agreementId"
                | "titleName"
                | "workName"
                | "transfereeName"
                | "companyId"
                | "transfereeAvatarUrl"
                | "totalGrossAmount"
                | "totalNetAmount"
                | "isDeleted"
            >;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + `/transfer-term`, data);
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Update transfer term
export const updateTransferTerm = createAsyncThunk(
    "transferTerms/updateTransferTerm",
    async (
        {
            transferTermId,
            data,
            sales,
        }: {
            transferTermId: number;
            data: Omit<
                ITransferTerm,
                | "id"
                | "createdAt"
                | "modifiedAt"
                | "agreementId"
                | "titleName"
                | "workName"
                | "transfereeName"
                | "companyId"
                | "transfereeAvatarUrl"
                | "totalGrossAmount"
                | "totalNetAmount"
                | "isDeleted"
            >;
            sales: ITransferTermSale[];
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/transfer-term/${transferTermId}`,
                data,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Delete transfer term
export const deleteTransferTerm = createAsyncThunk(
    "transferTerms/deleteTransferTerm",
    async ({ transferTermId }: { transferTermId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(
                import.meta.env.REACT_APP_API_ENDPOINT + `/transfer-term/${transferTermId}`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Fetch term agreement
export const fetchTransferTermAgreement = createAsyncThunk(
    "transferTerms/fetchTransferTermAgreement",
    async ({ transferTermId }: { transferTermId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/transfer-term/${transferTermId}/agreement`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Add term agreement
export const addTransferTermAgreement = createAsyncThunk(
    "transferTerms/addTransferTermAgreement",
    async (
        { data, transferTermId, signatureDate }: { data: FormData; transferTermId: number; signatureDate: Date | null },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + `/transfer-term/${transferTermId}/agreement`,
                data,
                {
                    params: { signatureDate },
                },
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Delete term agreement
export const deleteTransferTermAgreement = createAsyncThunk(
    "transferTerms/deleteTransferTermAgreement",
    async ({ transferTermId }: { transferTermId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(
                import.meta.env.REACT_APP_API_ENDPOINT + `/transfer-term/${transferTermId}/agreement`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Fetch sales of one transferTerm
export const fetchTransferTermSales = createAsyncThunk(
    "transferTerms/fetchTransferTermSales",
    async ({ transferTermId }: { transferTermId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT + `/transfer-term/${transferTermId}/sales`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

// Export all transferTerms
export const downloadTransferTerms = createAsyncThunk(
    "transferTerms/downloadTransferTerms",
    async (_, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/transfer-term/export`, {
                    headers: { Accept: "*/*" },
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );
            const fileName = `export_transfers_${getDateForFileName()}.xlsx`;
            downloadApiExport(response.data, fileName);
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export enum ETransferTermStatus {
    TO_SIGN = 0,
    EXPIRED_SOON = 1,
    UNDER_NEGOTIATION = 2,
    UP_TO_DATE = 3,
    EXPIRED = 4,
}

export interface ITransferTermAdvance {
    id: number | null;
    grossAmount: number;
    netAmount: number;
    isPaid: boolean | number;
    order: number;
    paymentDate: number | Date | null;
    status: ETransferTermSalesStatus;
}

export interface ITransferTerm {
    id: number;
    agreementId: number | null;
    advancePayment: ITransferTermAdvance[];
    /**
     * Start of the contract (can be way before next payment date)
     */
    startDate: number | Date;
    /**
     * End of contract.
     */
    expirationDate: number | Date;
    /**
     * First payment date (only asked in form, needed for payment dates calculations).
     */
    upcomingPaymentDate: number | Date;
    workId: number | null;
    workName?: string;
    workAvatarUrl?: string | null;
    titleId: number | null;
    titleAvatarUrl?: string | null;
    transfereeId: number;
    percentages: any; // We're not using percentages as of now.
    companyId: number;
    status: ETransferTermStatus;
    /**
     * Length of contract -- in months
     */
    periodDuration: number;
    /**
     * Delay between payment due and moment it's registered in accounting (?? not sure lol) -- in days
     */
    paymentDelay: number;
    createdAt: string;
    modifiedAt: string;
    titleName: string;
    transfereeAvatarUrl: string;
    transfereeName: string;
    countryCodes: string[];
    /**
     * Sum of all gross amounts on sales.
     */
    totalGrossAmount: number;
    /**
     * Sum of all net amounts on sales.
     */
    totalNetAmount: number;
    isDeleted: number;
    /**
     * Initial generated amount (only really useful on creation.)
     */
    initialGrossAmount: number;
    reference: string | null;
}

interface InitialState {
    transferTerm: ITransferTerm;
    transferTerms: ITransferTerm[];
    transferTermsCount: number;
    termsFetchStatus: TFetchStatus;
    transferTermAgreement: string;
    termSales: ITransferTermSale[];
    termSalesStatus: TFetchStatus;
}

const initialState: InitialState = {
    transferTerm: {} as ITransferTerm,
    transferTerms: [],
    transferTermsCount: 0,
    termsFetchStatus: "idle",
    transferTermAgreement: "",
    termSales: [],
    termSalesStatus: "idle",
};

export const transferTermsStore = createSlice({
    name: "transferTerms",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(fetchTransferTerms.fulfilled, (state, { payload }) => {
            state.transferTerms = payload.entities;
            state.transferTermsCount = payload.count;
            state.termsFetchStatus = "succeeded";
        });
        builder.addCase(fetchTransferTerms.pending, (state) => {
            state.termsFetchStatus = "pending";
        });
        builder.addCase(fetchTransferTerms.rejected, (state, { payload }) => {
            state.termsFetchStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchTransferTerm.fulfilled, (state, { payload }) => {
            state.transferTerm = payload;
        });
        builder.addCase(fetchTransferTerm.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(createTransferTerm.fulfilled, (state, { payload }) => {
            state.transferTerms = [...state.transferTerms, payload];
            toast.success(`${t("TransferTerms.Store.TermCreationSuccess")}`);
        });
        builder.addCase(createTransferTerm.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(updateTransferTerm.fulfilled, (state, action) => {
            const request = action.meta.arg.data;
            const termNetSales = action.meta.arg.sales.reduce((a, { netAmount }) => a + netAmount, 0);
            const termGrossSales = action.meta.arg.sales.reduce((a, { grossAmount }) => a + grossAmount, 0);
            const oldTerm = state.transferTerms.find((item) => item.id === action.payload.id);
            const sumNetAdvance = request.advancePayment.reduce((a, { netAmount }) => a + netAmount, 0);

            if (oldTerm) {
                const updatedTerm = {
                    ...action.payload,
                    expirationDate: new Date(action.payload.expirationDate),
                    comingSalesState: new Date(action.payload.comingSalesState),
                    // Because they might change the initialGrossAmount or the advance, I need to calculate these values again.
                    totalGrossAmount: action.payload.initialGrossAmount + termGrossSales,
                    totalNetAmount:
                        action.payload.initialGrossAmount - sumNetAdvance > 0
                            ? action.payload.initialGrossAmount - sumNetAdvance + termNetSales
                            : termNetSales,
                };
                state.transferTerms = state.transferTerms.map((term) => {
                    if (term.id === updatedTerm.id) {
                        return { ...term, ...updatedTerm };
                    } else {
                        return term;
                    }
                });
            }
            toast.success(`${t("TransferTerms.Store.TermUpdateSuccess")}`);
        });
        builder.addCase(updateTransferTerm.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(deleteTransferTerm.fulfilled, (state, action) => {
            state.transferTerms = state.transferTerms.filter((term) => term.id !== action.meta.arg.transferTermId);
            toast.success(`${t("TransferTerms.Store.TermDeleteSuccess")}`);
        });
        builder.addCase(deleteTransferTerm.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchTransferTermAgreement.fulfilled, (state, { payload }) => {
            state.transferTermAgreement = payload;
        });
        builder.addCase(fetchTransferTermAgreement.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(addTransferTermAgreement.fulfilled, (state, { payload }) => {
            state.transferTerms = state.transferTerms.map((term) => {
                if (term.id === payload.id) {
                    return { ...term, agreementId: payload.agreementId, storageId: payload.storageId };
                } else {
                    return term;
                }
            });
            toast.success(`${t("TransferTerms.Store.AddAgreementSuccess")}`);
        });
        builder.addCase(addTransferTermAgreement.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(deleteTransferTermAgreement.fulfilled, (state, action) => {
            state.transferTerms = state.transferTerms.map((term) => {
                if (term.id === action.meta.arg.transferTermId) {
                    return { ...term, agreementId: null };
                } else {
                    return term;
                }
            });
            toast.success(`${t("TransferTerms.Store.DeleteAgreementSuccess")}`);
        });
        builder.addCase(deleteTransferTermAgreement.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchTransferTermSales.fulfilled, (state, { payload }) => {
            state.termSales = payload;
            state.termSalesStatus = "succeeded";
        });
        builder.addCase(fetchTransferTermSales.pending, (state) => {
            state.termSalesStatus = "pending";
        });
        builder.addCase(fetchTransferTermSales.rejected, (state, { payload }) => {
            state.termSalesStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
    },
});

export default transferTermsStore.reducer;
